/**
 * @author NikolaV
 */

import { MapWrapper } from '@openlayers/MapWrapper';

// ol imports
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OlOverlay from 'ol/Overlay';
import OlDraw from 'ol/interaction/Draw';
import { Type as OlGeometryType } from 'ol/geom/Geometry';
import * as olStyle from 'ol/style';
import Feature from 'ol/Feature';


export class DrawInteractionTool implements MapTool {
    geometryType: OlGeometryType = 'Polygon';

    geometryListener = null;

    finishedStyle = new olStyle.Style({
        fill: new olStyle.Fill({
            color: 'rgba(255, 120, 20, 0.33)'
        }),
        stroke: new olStyle.Stroke({
            color: '#FF581C',
            width: 1
        }),
        image: new olStyle.Circle({
            radius: 7,
            fill: new olStyle.Fill({
                color: '#FC3'
            })
        })
    });

    inProgressStyle = new olStyle.Style({
        fill: new olStyle.Fill({
            color: 'rgba(255, 255, 255, 0.3)'
        }),
        stroke: new olStyle.Stroke({
            color: 'rgba(0, 0, 0, 0.5)',
            lineDash: [10, 10],
            width: 1
        }),
        image: new olStyle.Circle({
            radius: 5,
            stroke: new olStyle.Stroke({
                color: 'rgba(0, 0, 0, 0.7)'
            }),
            fill: new olStyle.Fill({
                color: 'rgba(255, 255, 255, 0.2)'
            })
        })
    });

    lineClickArea = new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: [0, 0, 0, 0.01],
            width: 15
        })
    });

    drawLayer = new VectorLayer({ // layer for finished drawings or additional features, not for in-progress drawing
        source: new VectorSource({ wrapX: false })
    });

    drawInteraction: OlDraw;
    featureLabelsMap: Map<Feature, OlOverlay[]> = new Map(); // track labels for each feature
    inProgressLabel: OlOverlay = null; // remove only this label when tool is deactivated, clear all when tool is disabled

    constructor(
        public mapWrapper: MapWrapper,
        public onDrawEndCallback?: (feature: Feature) => void,
        public onDrawProgressCallback?: (feature: any) => void,
        public onDrawRemove?: (features: Feature) => void,
    ) {
    }

    onDrawStart(e) {
        if (this.onDrawProgressCallback) {
            this.onDrawProgressCallback(e.feature);
        }
    }

    onDrawEnd(e?: { feature: Feature }) {
        this.inProgressLabel = null;
        if (e?.feature) {
            e.feature.set('finished', true);
        }
        if (this.onDrawEndCallback) {
            this.onDrawEndCallback(e?.feature);
        }
        document.querySelectorAll('.measure-label').forEach(el => (el as HTMLDivElement).parentElement.style.pointerEvents = 'auto')
    }

    activate() {
        if (!this.drawInteraction) {
            this.drawInteraction = new OlDraw({
                type: this.geometryType,
                source: this.drawLayer.getSource(),
                style: this.inProgressStyle,
                stopClick: true,
                condition: event => {
                    return event.originalEvent.button !== 2;
                }
            });

            this.drawInteraction.on('drawstart', this.onDrawStart.bind(this));
            this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));
        }
        this.mapWrapper.getInstance().addInteraction(this.drawInteraction);

        this.drawLayer.getSource().on('removefeature', (event) => {
            if (event.feature) {
                this.clearFeatureLabels(event.feature);
            }
        });

        this.drawLayer.setStyle([this.finishedStyle, this.lineClickArea]);
        this.mapWrapper.addLayer(this.drawLayer, 98, 'tool-draw');
        this.drawInteraction.setActive(true);
    }

    deactivate() {
        if (this.drawInteraction) {
            (this.drawInteraction.getOverlay().getSource() as VectorSource).forEachFeature(f => this.clearFeatureLabels(f));
            this.drawInteraction.setActive(false);
        }

        this.mapWrapper.getInstance().removeInteraction(this.drawInteraction);

        if (this.inProgressLabel) {
            this.mapWrapper.getInstance().removeOverlay(this.inProgressLabel);
        }
    }

    clear(removeLayer = true) {
        this.featureLabelsMap.forEach((labels, feature) => this.clearFeatureLabels(feature));
        this.featureLabelsMap.clear();

        if (this.onDrawRemove) {
            this.drawLayer.getSource().forEachFeature(f => {
                this.onDrawRemove(f);
            });
        }
        this.drawLayer.getSource().clear();
        if (removeLayer) {
            this.mapWrapper.removeLayer(this.drawLayer);
        }
    }

    createLabel(feature: Feature, altStyle: boolean = false): OlOverlay {
        const $textElem = $('<div class="measure-label no-break-text">');
        $textElem.css({
            'background-color': 'rgba(255, 255, 255, 0.5)',
            'padding': '0px 4px',
            'border-radius': '3px',
            'font-size': altStyle ? '12px' : '14px'
        });

        const label = new OlOverlay({
            element: $textElem[0] as HTMLDivElement,
            positioning: altStyle ? 'center-center' : 'bottom-center',
            stopEvent: false
        });

        if (feature) {
            if (!this.featureLabelsMap.has(feature)) {
                this.featureLabelsMap.set(feature, []);
            }
            this.featureLabelsMap.get(feature).push(label);
        }

        return label;
    }

    clearFeatureLabels(feature: Feature) {
        if (this.featureLabelsMap.has(feature)) {
            const featureLabels = this.featureLabelsMap.get(feature);
            featureLabels.forEach(label => {
                this.mapWrapper.getInstance().removeOverlay(label);
            });
            this.featureLabelsMap.delete(feature);
        }
    }

    cancelCurrentDrawing() {
        const countBefore = this.drawLayer.getSource().getFeatures().length;

        try { // OL bug, but should work anyway
            if (this.drawInteraction.getActive()) {
                this.drawInteraction.finishDrawing();
            }
        } catch (e) {
        }

        const src = this.drawLayer.getSource();
        const features = src.getFeatures();

        if (features.length > countBefore) {
            const feature = features[features.length - 1];
            src.removeFeature(feature);
        }
    }

    public getDrawingsCount(): number {
        if (!this.drawLayer || !this.drawLayer.getSource) return 0;
        return this.drawLayer.getSource().getFeatures().length;
    }

    public removeMeasurement = (feature: Feature) => {
        if (!feature) return
        this.drawLayer.getSource().removeFeature(feature);
        if (this.onDrawRemove) {
            this.onDrawRemove(feature);
        }
    }

    getFeatureAtCoordinate = (coordinate: number[]): Feature => {
        const features = this.mapWrapper.getInstance().getFeaturesAtPixel(
            this.mapWrapper.getInstance().getPixelFromCoordinate(coordinate),
            {
                hitTolerance: 3,
                layerFilter: (layer) => {
                    return ['logpoint', 'pipe', 'model_point'].includes(layer.get('id'));
                }
            }
        );

        if (features && features.length > 0) {
            const pointFeatures = features.filter(f => {
                return f.getGeometry().getType() === 'Point' && ($.isNumeric(f.get('data')?.z) || $.isNumeric(f.get('data')?.elevation));
            });
            return pointFeatures[0] as Feature;
        }

        return null;
    }

    formatLength = (length) => {
        let output = '';

        if (kuura.getProjectProj4Code() === 'EPSG:4326') {
            output += '~';
        }
        if (length > 1000) {
            output += `${Math.round(length) / 1000} km`;
        } else {
            output += `${Math.round(length * 100) / 100} m`;
        }
        return output;
    }

    getLabels(): OlOverlay[] {
        const labels: OlOverlay[] = [];
        this.featureLabelsMap.forEach((value, key) => {
            labels.push(...value);
        });
        return labels;
    }
}
